/**
 * The trot (design D6): a stride phase driven by ground covered, not by the clock, so a
 * stopped dog's legs freeze and a moving one's paws never skate. Diagonal pairs — the
 * cheapest thing to compute that unmistakably reads as four-legged — with front-left and
 * back-right sharing a phase and front-right and back-left half a cycle off. The pairing
 * is read off `ASSEMBLIES`' socket order rather than hardcoded, so a change to that order
 * changes the trot with it.
 *
 * Pure trigonometry, no allocation per call: `gaitPose` mutates and returns a single
 * module-owned pose object, the same allocation-free idiom as `stepFollow`'s caller-owned
 * one — there is exactly one following dog, so one shared result is all this ever needs.
 */

import { ASSEMBLIES } from './assembly';

/**
 * Metres of ground per full leg cycle. Pinned by design D6: a stopped dog has still legs
 * (nothing to integrate this against) and a moving one plants a paw once every 0.55m,
 * which is what "not skating" means and why the phase is driven by distance rather than
 * `delta`.
 */
export const STRIDE_LENGTH = 0.55;

const TWO_PI = Math.PI * 2;

/** How far the hip swings, in radians. */
const HIP_AMPLITUDE = 0.5;

/** How far the knee flexes over the swing half, in radians. */
const KNEE_AMPLITUDE = 0.6;

/** Vertical body bob, in metres — a trot bobs on each diagonal beat. */
const BODY_BOB_AMPLITUDE = 0.02;

/** A small side-to-side roll, in radians. */
const BODY_ROLL_AMPLITUDE = 0.05;

/** Head bob, counter-phase to the body's, in metres. */
const HEAD_BOB_AMPLITUDE = 0.015;

/** How far the tail swings, in radians. */
const TAIL_AMPLITUDE = 0.6;

export type LegId = 'front-left' | 'front-right' | 'back-left' | 'back-right';

/**
 * The dog body's socket ids, in `ASSEMBLIES`' fixed order — the source of the pairing, and
 * exported so the renderer's one set of limb refs is keyed off the same list rather than a
 * second hardcoded copy of it.
 */
export const LEG_IDS = ASSEMBLIES['robot-dog-body']!.sockets.map((socket) => socket.id) as LegId[];

/** front-left & back-right share a phase; front-right & back-left are half a cycle off. */
function phaseOffsetFor(legId: LegId): number {
  return legId === 'front-left' || legId === 'back-right' ? 0 : 0.5;
}

function isHindLeg(legId: LegId): boolean {
  return legId.startsWith('back');
}

/** Wraps a phase (in cycles) into [0, 1). */
function wrapPhase(phase: number): number {
  return phase - Math.floor(phase);
}

export type LegPose = {
  hip: number;
  knee: number;
};

export type GaitPose = {
  legs: Record<LegId, LegPose>;
  bodyBob: number;
  bodyRoll: number;
  headBob: number;
  tailAngle: number;
};

function createGaitPose(): GaitPose {
  const legs = {} as Record<LegId, LegPose>;
  for (const id of LEG_IDS) legs[id] = { hip: 0, knee: 0 };
  return { legs, bodyBob: 0, bodyRoll: 0, headBob: 0, tailAngle: 0 };
}

/** The one live pose, mutated in place every call — see the file comment. */
const POSE = createGaitPose();

/**
 * One leg's hip and knee from its own phase (in cycles): a hip swing on a plain `sin`, and
 * a knee flex on the swing half only (`max(0, -cos)`), so the paw lifts clear of the
 * ground going forward and the leg is near straight while it carries weight. Hind knees
 * bend the opposite way to front knees — the stifle, the single detail that most makes a
 * silhouette read as a dog rather than a table.
 */
function legAngles(target: LegPose, phase: number, isHind: boolean): void {
  const theta = phase * TWO_PI;
  target.hip = HIP_AMPLITUDE * Math.sin(theta);
  const swing = Math.max(0, -Math.cos(theta));
  target.knee = (isHind ? -1 : 1) * KNEE_AMPLITUDE * swing;
}

/**
 * The dog's pose at this stride phase (in cycles of `STRIDE_LENGTH`) and this wag phase.
 * The two phases are deliberately separate: a walking dog's tail rides the stride, but a
 * standing dog still wags, so its caller advances the wag phase on `delta` while at rest
 * instead — a wag is not a footfall.
 */
export function gaitPose(stridePhase: number, wagPhase: number): GaitPose {
  for (const id of LEG_IDS) {
    const phase = wrapPhase(stridePhase + phaseOffsetFor(id));
    legAngles(POSE.legs[id], phase, isHindLeg(id));
  }

  // Two bob cycles per stride: a trot bobs on each diagonal beat.
  POSE.bodyBob = BODY_BOB_AMPLITUDE * Math.sin(stridePhase * TWO_PI * 2);
  POSE.bodyRoll = BODY_ROLL_AMPLITUDE * Math.sin(stridePhase * TWO_PI);
  POSE.headBob = HEAD_BOB_AMPLITUDE * Math.sin(stridePhase * TWO_PI * 2 + Math.PI);
  POSE.tailAngle = TAIL_AMPLITUDE * Math.sin(wagPhase * TWO_PI);

  return POSE;
}
